import type { ColumnSpec } from "../components/settings/columnSpec";
import {
  COLUMNS_VERSION,
  DEFAULT_COLUMNS,
  migrateColumns,
} from "../components/settings/columnSpec";
import { decodeMemory, encodeMemory } from "./codec";
import type { SetURLSearchParams } from "react-router";
import {
  validateMemory,
  type Memory,
  type GridStateMap,
  type EmbeddedDictionary,
  type TimelineUrlState,
} from "./memory-schema";
import { ViewMode } from "../domain";
import { parseLensParam } from "../lenses/lensParam";
import {
  encodeCodeState,
  decodeCodeState,
} from "../lenses/code/codeStateCodec";
import type { DataDictionary } from "./data-dictionary";
import { newDataDictionary } from "./data-dictionary";
import {
  buildDataDictionary,
  findStoredDictionaryByContent,
} from "./dictionary-storage";

export type { Memory, GridStateMap, EmbeddedDictionary, TimelineUrlState };

// Soft cap for the encoded `?state=` value. Past this size, browsers and CDNs
// start truncating or rejecting URLs. We still write the URL — but surface a
// warning so the user knows the shared link may not survive all clients.
export const URL_STATE_SOFT_LIMIT = 30_000;

export const onSearchParamsChange = (
  searchParams: URLSearchParams,
  setFixValue: (value: string) => void,
  setColumns: (value: ColumnSpec[]) => void,
  setTitle: (value: string) => void,
  defaultTitle: string,
  currentDictionaryContent: string | undefined,
  setDataDictionary: (dd: DataDictionary) => void,
  setSplitPaneLeftPct: (v: number | undefined) => void,
  setViewMode: (v: ViewMode) => void,
  setSelectedRowIndices: (v: number[]) => void,
  setGridStates: (v: GridStateMap) => void,
  setExcludeDateFromMessageTime: (v: boolean) => void,
  setTimelineState: (v: TimelineUrlState | undefined) => void,
  setValidationError: (msg: string | undefined) => void,
  setCodeState: (v: string | undefined) => void = () => undefined,
) => {
  const resetToDefaults = () => {
    setFixValue("");
    setColumns([...DEFAULT_COLUMNS]);
    setTitle(defaultTitle);
    if (currentDictionaryContent !== undefined) {
      setDataDictionary(newDataDictionary());
    }
    setSplitPaneLeftPct(undefined);
    setViewMode(ViewMode.VIEW);
    setSelectedRowIndices([]);
    setGridStates({});
    setExcludeDateFromMessageTime(false);
    setTimelineState(undefined);
    setCodeState(undefined);
  };

  try {
    const encoded = searchParams.get("state");
    if (encoded && encoded.length > 0) {
      const parsed: unknown = decodeMemory(encoded);
      const result = validateMemory(parsed);
      if (!result.ok) {
        console.warn("Memory validation failed:", result.error);
        setValidationError(result.error);
        resetToDefaults();
        return;
      }
      const memory = result.value;
      setFixValue(memory.fixMessages);
      setColumns(migrateColumns(memory.columns, memory.columnsVersion));
      const t = (memory.title ?? "").trim();
      setTitle(t.length > 0 ? t : defaultTitle);

      if (memory.dictionary) {
        if (memory.dictionary.content !== currentDictionaryContent) {
          const match = findStoredDictionaryByContent(
            memory.dictionary.content,
          );
          if (match) {
            setDataDictionary(
              buildDataDictionary(
                match.filename,
                match.content,
                "local",
                match.id,
              ),
            );
          } else {
            setDataDictionary(
              buildDataDictionary(
                memory.dictionary.filename,
                memory.dictionary.content,
                "url",
              ),
            );
          }
        }
      } else if (currentDictionaryContent !== undefined) {
        setDataDictionary(newDataDictionary());
      }

      setSplitPaneLeftPct(memory.splitPaneLeftPct ?? undefined);
      setViewMode(memory.viewMode ?? ViewMode.VIEW);
      setSelectedRowIndices(memory.selectedRowIndices ?? []);
      setGridStates(memory.gridStates ?? {});
      setExcludeDateFromMessageTime(memory.excludeDateFromMessageTime ?? false);
      setTimelineState(memory.timeline ?? undefined);
      setCodeState(
        decodeCodeState({ code: memory.code, codeRef: memory.codeRef }),
      );
    } else {
      resetToDefaults();
      // Honour a bare `?lens=` deep link when there is no compressed state.
      const lens = searchParams.get("lens");
      if (lens) setViewMode(parseLensParam(lens));
    }
  } catch (e) {
    console.warn(e);
    setValidationError("Could not decode URL state");
    resetToDefaults();
  }
};

export const onDataUpdate = (
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  fixValue: string,
  columns: ColumnSpec[],
  title: string,
  dictionary: EmbeddedDictionary | undefined,
  splitPaneLeftPct: number | undefined,
  viewMode: ViewMode,
  selectedRowIndices: number[],
  gridStates: GridStateMap,
  excludeDateFromMessageTime: boolean,
  timeline: TimelineUrlState | undefined,
  setSizeWarning?: (msg: string | undefined) => void,
  currentEncodedOverride?: string,
  codeState?: string | undefined,
) => {
  try {
    const next = new URLSearchParams(searchParams);

    const hasGridStates = Object.keys(gridStates).length > 0;
    const encodedCode = encodeCodeState(codeState);
    const memory: Memory = {
      fixMessages: fixValue,
      columns: columns,
      columnsVersion: COLUMNS_VERSION,
      title: (title || getInitialTitle()).trim(),
      ...(dictionary ? { dictionary } : {}),
      ...(splitPaneLeftPct !== undefined ? { splitPaneLeftPct } : {}),
      viewMode,
      selectedRowIndices,
      ...(hasGridStates ? { gridStates } : {}),
      ...(excludeDateFromMessageTime
        ? { excludeDateFromMessageTime: true }
        : {}),
      ...(timeline ? { timeline } : {}),
      ...(encodedCode.code !== undefined ? { code: encodedCode.code } : {}),
      ...(encodedCode.codeRef !== undefined
        ? { codeRef: encodedCode.codeRef }
        : {}),
    };

    const encoded = encodeMemory(memory);
    const currentEncoded =
      currentEncodedOverride ?? searchParams.get("state") ?? "";
    if (encoded) {
      if (encoded === currentEncoded) return undefined;
      next.set("state", encoded);
      if (setSizeWarning) {
        setSizeWarning(
          encoded.length > URL_STATE_SOFT_LIMIT
            ? `Shared URL is ${encoded.length.toLocaleString()} chars — may exceed browser/CDN limits.`
            : undefined,
        );
      }
    } else {
      if (!currentEncoded) return undefined;
      next.delete("state");
      setSizeWarning?.(undefined);
    }

    let currentMemory: Memory | undefined;
    if (currentEncoded) {
      try {
        const currentResult = validateMemory(decodeMemory(currentEncoded));
        if (currentResult.ok) currentMemory = currentResult.value;
      } catch {
        currentMemory = undefined;
      }
    }
    const currentNavigationKey = currentMemory
      ? `${currentMemory.viewMode ?? ViewMode.VIEW}|${currentMemory.timeline?.view ?? ""}`
      : "";
    const nextNavigationKey = `${memory.viewMode ?? ViewMode.VIEW}|${memory.timeline?.view ?? ""}`;

    setSearchParams(next, {
      replace: currentNavigationKey === nextNavigationKey,
    });
    return encoded || "";
  } catch (e) {
    console.warn(e);
    return undefined;
  }
};

export const getInitialTitle = (): string => {
  if (typeof document !== "undefined") {
    const current = (document.title || "").trim();
    return current || "FIX Parser α";
  }
  return "FIX Parser α";
};
